import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home, Trophy, History, User, Shield, LogOut, BadgePercent, Video, Crown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Home", icon: Home, testId: "nav-home-link", end: true },
  { to: "/leaderboard", label: "Classifiche", icon: Trophy, testId: "nav-leaderboard-link" },
  { to: "/vip", label: "VIP", icon: Crown, testId: "nav-vip-link" },
  { to: "/bonus", label: "Bonus", icon: BadgePercent, testId: "nav-bonus-link" },
  { to: "/saluti", label: "Saluti", icon: Video, testId: "nav-saluti-link" },
  { to: "/history", label: "Storico", icon: History, testId: "nav-history-link" },
  { to: "/profile", label: "Profilo", icon: User, testId: "nav-profile-link" },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 tw-grain">
      {/* Top bar (fixed) */}
      <header className="fixed inset-x-0 top-0 z-50 glass border-b border-slate-200">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <NavLink to="/" data-testid="topbar-logo-link" className="flex items-center gap-2">
            <Logo size="text-2xl" />
          </NavLink>

          {/* Desktop horizontal nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-full px-3 py-2 text-sm font-bold transition-colors ${
                    isActive ? "bg-zinc-950 text-green-400" : "text-zinc-600 hover:bg-slate-100"
                  }`
                }
              >
                <item.icon className="h-4 w-4" /> {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && user.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link"
                className="flex items-center gap-1.5 rounded-full bg-zinc-950 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-green-400 transition-colors hover:bg-zinc-800"
              >
                <Shield className="h-3.5 w-3.5" /> Admin
              </NavLink>
            )}
            <Button data-testid="logout-button" onClick={doLogout} variant="ghost" size="sm" className="rounded-full text-zinc-600 hover:text-zinc-950">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content — padding clears fixed top bar and mobile bottom bar */}
      <main className="mx-auto max-w-5xl px-4 pb-28 pt-20 sm:px-6 md:pb-12">
        <Outlet />
      </main>

      {/* Bottom nav (mobile only) */}
      <nav className="fixed inset-x-0 bottom-0 z-50 glass border-t border-slate-200 pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="mx-auto flex max-w-5xl items-stretch justify-around">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testId}-mobile`}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                  isActive ? "text-green-600" : "text-zinc-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`rounded-full p-1.5 transition-all ${isActive ? "bg-green-500/15" : ""}`}>
                    <item.icon className="h-5 w-5" />
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
